import { Button, Form, DatePicker, message } from "antd";
import dayjs from "dayjs";
import fileDownload from "js-file-download";
import { useState, useEffect } from "react";
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import "./EALMBonusIncentive.scss";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import ContentTitle from "../../../components/ui/ContentTitle";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import {
  __currentUser__,
  __jwtToken__,
  __projectKey__,
} from "../../../graphql/policies";
import { ServiceTypes } from "../../../types/types";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface EALMBonusIncentiveProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const EALMBonusIncentive = ({
  setSelectedService,
  setSelectSubItem,
}: EALMBonusIncentiveProps) => {
  const [form] = Form.useForm();
  const [isBonusLoading, setIsBonusLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const currentUser = useReactiveVar(__currentUser__);
  const jwtToken = useReactiveVar(__jwtToken__);
  const projectKey = useReactiveVar(__projectKey__);
  const { data: userPrograms, loading: userProgramsLoading } =
    useGetUserProgramsByServiceQuery({
      variables: {
        userId: parseInt(renderDecodedId(currentUser?.id)),
        serviceId: 4,
      },
    });

  useEffect(() => {
    setSelectedService(ServiceTypes.MKT);
    setSelectSubItem("EALM Bonus Incentive");
  }, []);

  const handleBonusSubmit = () => {
    form
      .validateFields()
      .then(async () => {
        setIsBonusLoading(true);
        const { yyyymm } = form.getFieldsValue();
        const header = {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Project-Id": "AF",
            "X-Authorization-Provider": "Anonymous",
            "X-Project-Key": projectKey,
          },
          responseType: "blob",
        };
        await apiCall({
          method: "POST",
          header: header,
          endPoint: process.env.REACT_APP_GET_EALM_BONUS_INCENTIVE,
          payload: {
            yyyymm: dayjs(yyyymm).format("YYYY-MM"),
            user: currentUser?.userEmail,
          }
        });
        message.success("Successfully");
        setIsBonusLoading(false);
      })
      .catch((e) => console.log(e));
  };

  const handleReportSubmit = () => {
    console.log(parseInt(renderDecodedId(currentUser?.id)));
    form
      .validateFields()
      .then(async () => {
        setIsReportLoading(true);
        const { yyyymm } = form.getFieldsValue();
        const header = {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            "X-Project-Id": "AF",
            "X-Authorization-Provider": "Anonymous",
            "X-Project-Key": projectKey,
          },
          responseType: "blob",
        };
        const data = await apiCall({
          method: "GET",
          header: header,
          endPoint:
            process.env.REACT_APP_GET_EALM_BONUS_INCENTIVE_REPORT +
            dayjs(yyyymm).format("YYYY-MM") +
            "/" +
            currentUser?.userEmail,
        });
        if (data) fileDownload(data, "EALM_bonusIncentive.xlsx");
        else message.error("The specified month has not yet been settled.");

        setIsReportLoading(false);
      })
      .catch((e) => console.log(e));
  };

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "EALM Bonus Incentive"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="ealmBonusIncentiveWrapper">
      <ContentTitle title="EALM積點奬勵" />
      <div className="table-container">
        <Form form={form} name="ealm-bonus-form" className="form">
          <div className="flex">
            <Form.Item
              name="yyyymm"
              rules={[requiredRule]}
              label="Year month"
              className="item"
            >
              <DatePicker
                format="YYYY-MMM"
                picker="month"
                className="w-full"
                disabledDate={(current) => {
                  return (
                    current && current > dayjs().endOf("month").subtract(1, "month")
                  );
                }}
              />
            </Form.Item>
            <Button
              type="primary"
              className="ms-5"
              onClick={handleBonusSubmit}
              loading={isBonusLoading}
            >
              產生積點獎勵
            </Button>
            <Button
              type="primary"
              className="ms-5"
              onClick={handleReportSubmit}
              loading={isReportLoading}
            >
              匯出購物金明細表
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EALMBonusIncentive;
